import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
  type AuthUser,
} from './AuthContext'
import { isEnvReady } from '@/lib/env'
import { useOnboardingStore } from '@/lib/onboarding/store'

/**
 * Top-level auth state holder. Configures Amplify exactly once at mount,
 * then keeps `status` and `user` in sync with the Cognito session:
 *
 *  - On mount, attempt `getCurrentUser()` + `fetchAuthSession()` to restore
 *    any persisted session (Amplify stores refresh tokens in localStorage).
 *  - Subscribe to the Hub auth channel so external sign-in/out (e.g. the
 *    OAuth callback page calling `signInWithRedirect`) propagates here.
 *  - Expose `signOut` and a manual `refresh` for callers that just changed
 *    profile data and want the cached email/id rehydrated.
 *
 * Status starts as `loading`. If the env isn't configured (developer hitting
 * the landing page without populated VITE_COGNITO_*), it transitions to
 * `unconfigured` and the auth pages can render an explanatory message
 * instead of crashing.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<AuthUser | null>(null)

  const refresh = useCallback(async () => {
    try {
      const { fetchAuthSession, getCurrentUser } = await import('aws-amplify/auth')
      const session = await fetchAuthSession()
      const idToken = session.tokens?.idToken
      if (!idToken) {
        setUser(null)
        setStatus('unauthenticated')
        return
      }
      const current = await getCurrentUser()
      const payload = idToken.payload
      // `cognito:groups` is either a JSON array OR (older deployments) a
      // comma-separated string — handle both so the admin gate works in
      // every environment.
      const rawGroups = payload['cognito:groups']
      const groups = Array.isArray(rawGroups)
        ? rawGroups.filter((g): g is string => typeof g === 'string')
        : typeof rawGroups === 'string'
          ? rawGroups
              .split(',')
              .map((g) => g.trim())
              .filter(Boolean)
          : []
      setUser({
        id: current.userId,
        email: (payload.email as string | undefined) ?? '',
        // `identities` is present on the ID token when the user came in via
        // a federated provider. Cognito-native users have no `identities`.
        isFederated: Array.isArray(payload.identities) && payload.identities.length > 0,
        groups,
      })
      setStatus('authenticated')
    } catch {
      setUser(null)
      setStatus('unauthenticated')
    }
  }, [])

  const signOut = useCallback(async () => {
    const { signOut: amplifySignOut } = await import('aws-amplify/auth')
    await amplifySignOut()
    // Wipe any wizard state from the previous session — the next user on
    // this browser shouldn't inherit a half-finished onboarding store.
    useOnboardingStore.getState().reset()
    setUser(null)
    setStatus('unauthenticated')
  }, [])

  useEffect(() => {
    if (!isEnvReady()) {
      setStatus('unconfigured')
      return
    }

    // E2E test escape hatch — Playwright seeds a fake session via
    // `window.__e404E2EAuth` before navigation; when present in a
    // dev build we skip Amplify entirely and admit the seeded user.
    // Gated on `import.meta.env.DEV` so production builds refuse it
    // even if a malicious script tries to set the flag.
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      const seed = (window as unknown as { __e404E2EAuth?: AuthUser }).__e404E2EAuth
      if (seed) {
        setUser(seed)
        setStatus('authenticated')
        return
      }
    }

    // Amplify is ~the largest dependency in the app and NOTHING on the public
    // marketing pages needs it — no landing, explore or pricing surface calls
    // useAuth. Statically importing it here put it in the entry graph, so Vite
    // emitted a modulepreload for it on every route and it competed with the
    // HTML and CSS that paint the hero. Measured LCP was 7.2s.
    //
    // Loading it here instead keeps it off the critical path for visitors who
    // never sign in, and costs signed-in users one extra round trip on a
    // status that already starts as 'loading'.
    let cancelled = false
    let unsubscribe: (() => void) | undefined

    void (async () => {
      const [{ Hub }, { configureAmplify }, { registerSessionInvalidator }] =
        await Promise.all([
          import('aws-amplify/utils'),
          import('./amplify'),
          import('@/lib/api/client'),
        ])
      if (cancelled) return

      // Must precede any session call — refresh() below depends on it.
      configureAmplify()
      // When the API client decides the session is dead (refresh failed),
      // tear down local state so the route guard bounces to /login.
      registerSessionInvalidator(() => {
        setUser(null)
        setStatus('unauthenticated')
      })
      void refresh()

      // Hub fires on signedIn, signedOut, tokenRefresh, signInWithRedirect
      // completions, and various failures. Refresh the cached user on the
      // events that change identity state.
      const un = Hub.listen('auth', ({ payload }) => {
        switch (payload.event) {
          case 'signedIn':
          case 'signInWithRedirect':
            void refresh()
            // Record terms consent server-side — the user just ticked the
            // page-level gate, so we stamp the current terms+privacy version
            // on their profile. Mirrors Android's behaviour of calling
            // /users/me/accept-terms after every successful auth. Fire-and-
            // forget — a failure here shouldn't block the sign-in.
            void import('@/lib/api/users').then((m) => m.acceptTerms()).catch(() => null)
            break
          case 'tokenRefresh':
            void refresh()
            break
          case 'signedOut':
            setUser(null)
            setStatus('unauthenticated')
            break
          default:
        }
      })

      // An unmount during the dynamic import must still tear the listener
      // down, or a stale subscription outlives the provider.
      if (cancelled) {
        un()
        return
      }
      unsubscribe = un
    })()

    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [refresh])

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, refresh, signOut }),
    [status, user, refresh, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
